import {
  EXTENSIONS_SELF_REPORTED,
  EXTENSION_URL,
} from '@constants/extension-codes';

import type { MedicationValues } from '@models/medication';
import {
  MEDICATION_CATEGORY_MAP,
  MedicationStatementRecord,
} from '@models/medication';

import { stepsToDosage } from '@utils/fhir-mappers/dosageSteps';
import { ingredientRowsToContained } from '@utils/fhir-mappers/medicationIngredients';
import { formatPatientId } from '@utils/format';

import type {
  Extension,
  MedicationStatementModelInput,
  ResourceInput,
} from 'generated/types';
import { StatementStatus } from 'generated/types';

export const getMedicationTemplate = (
  patient_id: string,
  values: MedicationValues
): MedicationStatementModelInput => {
  const extensions: Extension[] = [];
  if (values.selfReported) {
    extensions.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensions.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  // Build the (possibly multi-step) structured dosage from the form steps.
  const dosage = stepsToDosage(values.dosageSteps, {
    route: values.route ?? undefined,
    instructions: values.instructions ?? undefined,
  });

  // New records are always stored in the reference model: a contained
  // `Medication` carrying the product name and each ingredient's strength, with
  // `medicationReference` pointing at it. `medicationCodeableConcept` is never
  // written — the two are members of the same FHIR choice type.
  const contained = ingredientRowsToContained(
    values.title,
    values.ingredients,
    values.doseForm
  );

  return {
    resourceType: 'MedicationStatement',
    subject: {
      reference: formatPatientId(patient_id),
    },
    ...(values.acquisitionType && {
      category: {
        coding: [
          {
            code: values.acquisitionType,
            display: MEDICATION_CATEGORY_MAP[values.acquisitionType],
            system: MedicationStatementRecord.VITALL_SYSTEM,
          },
        ],
        text: MEDICATION_CATEGORY_MAP[values.acquisitionType],
      },
    }),
    ...(dosage.length > 0 && {
      dosage,
    }),
    ...(values.startingDate &&
      !values.endingDate && {
        effectiveDateTime: values.startingDate,
      }),
    ...(values.startingDate &&
      values.endingDate && {
        effectivePeriod: {
          ...(values.startingDate && {
            start: values.startingDate,
          }),
          ...(values.endingDate && {
            end: values.endingDate,
          }),
        },
      }),
    ...(contained && {
      contained: [contained] as ResourceInput[],
      medicationReference: {
        reference: `#${contained.id}`,
        display: values.title,
      },
    }),
    status: values.status
      ? (values.status as StatementStatus)
      : StatementStatus.active,
    ...(extensions.length > 0 && {
      extension: extensions,
    }),
  };
};
